import { useContext, useEffect, useRef, useState } from 'react';
import { REFRESH_MS } from '@constants/timing';
import { useOnline } from '@lib/hooks/useOnline';
import { StreamContext } from './context';
import type { StreamArgs, StreamType } from './payloads';

export const useStreamStatus = () => useContext(StreamContext).connected;
/** Reader of the stream's freshness numbers (StreamStats); call it when rendering, e.g. on a tick. */
export const useStreamStats = () => useContext(StreamContext).stats;

/** Run `fn` on matching stream messages. `fn` may change every render. */
export function useStream<K extends StreamType>(types: K[], fn: (...args: StreamArgs<K>) => void) {
  const { subscribe } = useContext(StreamContext);
  const ref = useRef(fn);
  ref.current = fn;
  const key = types.join(',');
  useEffect(() => {
    const wanted = new Set(key.split(','));
    return subscribe((t, d) => {
      if (wanted.has(t)) (ref.current as (t: StreamType, d: unknown) => void)(t, d);
    });
  }, [subscribe, key]);
}

/**
 * Call `fn` at most once per `ms` after matching messages (refresh lists without hammering the API).
 * `filter` lets a page ignore messages about other records. A `reconnect` always reloads at once.
 */
export function useStreamRefresh<K extends StreamType>(types: K[], fn: () => void, ms: number = REFRESH_MS.default, filter?: (...args: StreamArgs<K>) => boolean) {
  const timer = useRef<number | undefined>(undefined);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const filterRef = useRef(filter);
  filterRef.current = filter;
  useStream<StreamType>([...types, 'reconnect'], (t, d) => {
    if (t === 'reconnect') {
      window.clearTimeout(timer.current);
      timer.current = undefined;
      fnRef.current();
      return;
    }
    const f = filterRef.current as ((t: StreamType, d: unknown) => boolean) | undefined;
    if (f && !f(t, d)) return;
    if (timer.current) return;
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      fnRef.current();
    }, ms);
  });
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      timer.current = undefined; // a re-run effect (StrictMode) must not see a dead timer and drop every message
    },
    [],
  );
}

export { useOnline };

/**
 * True when the device is offline, or the live stream has been down for `delayMs` (no flash on the first
 * connect or a blip). Clears as soon as both are back.
 */
export function useConnectionLost(delayMs: number) {
  const live = useStreamStatus();
  const online = useOnline();
  const [lost, setLost] = useState(false);
  // when the stream went down (its downtime counts from then, also across an offline spell)
  const downSince = useRef(Date.now());
  if (live) downSince.current = Date.now();
  useEffect(() => {
    if (live && online) {
      setLost(false);
      return;
    }
    if (!online) {
      setLost(true);
      return;
    }
    const left = delayMs - (Date.now() - downSince.current);
    setLost(left <= 0);
    if (left <= 0) return;
    const t = window.setTimeout(() => setLost(true), left);
    return () => window.clearTimeout(t);
  }, [live, online, delayMs]);
  return lost;
}
